// ./src/hooks/useDisplayLayouts.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  DisplayLayout,
  DisplayLayoutService,
  DisplayLayoutFilter,
  DisplayLayoutInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseDisplayLayoutsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<DisplayLayoutFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  /** Auto-select first layout after loading */
  autoSelectFirst?: boolean;

  userId?: string | null;
}

export interface UseDisplayLayoutsReturn {
  // Data state
  displayLayout: DisplayLayout | null;
  displayLayouts: DisplayLayout[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<DisplayLayoutFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingDisplayLayoutId: string | null;
  selectedDisplayLayout: DisplayLayout | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<DisplayLayoutFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<DisplayLayout[]>;
  create: (params: DisplayLayoutInput) => Promise<DisplayLayout | null>;
  update: (
    id: string,
    params: DisplayLayoutInput
  ) => Promise<DisplayLayout | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<DisplayLayoutFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedDisplayLayout: (displayLayout: DisplayLayout | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useDisplayLayouts = (
  options: UseDisplayLayoutsOptions = {}
): UseDisplayLayoutsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = false,
    isAuthenticated = false,
    autoSelectFirst = false,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const autoSelectFirstRef = useRef(autoSelectFirst);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    autoSelectFirstRef.current = autoSelectFirst;
  }, [autoSelectFirst]);

  // Data state
  const [displayLayout, setDisplayLayout] = useState<DisplayLayout | null>(
    null
  );
  const [displayLayouts, setDisplayLayouts] = useState<DisplayLayout[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<DisplayLayoutFilter>>(() => {
    const filter = { ...initialFilters };
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingDisplayLayoutId, setRemovingDisplayLayoutId] = useState<
    string | null
  >(null);
  const [selectedDisplayLayout, setSelectedDisplayLayout] =
    useState<DisplayLayout | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => displayLayouts.length === 0 && !loading,
    [displayLayouts.length, loading]
  );

  // Helper function to update display layout in list if it exists, or add it if not
  const updateOrAddDisplayLayoutInList = useCallback(
    (updatedLayout: DisplayLayout) => {
      setDisplayLayouts((prev) => {
        const existingIndex = prev.findIndex(
          (dl) => dl.id === updatedLayout.id
        );
        if (existingIndex >= 0) {
          const newLayouts = [...prev];
          newLayouts[existingIndex] = updatedLayout;
          return newLayouts;
        } else {
          return [updatedLayout, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove display layout from list if it exists
  const removeDisplayLayoutFromList = useCallback((layoutId: string) => {
    setDisplayLayouts((prev) => prev.filter((dl) => dl.id !== layoutId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    console.log("Resetting all the display layouts");
    setDisplayLayout(null);
    setDisplayLayouts([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingDisplayLayoutId(null);
    setSelectedDisplayLayout(null);
  }, []);

  // Get single display layout (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayLayoutService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const layoutData = result.data[0] as DisplayLayout;
          setDisplayLayout(layoutData);
          updateOrAddDisplayLayoutInList(layoutData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load display layout";
          setLastError(errorMessage);
          setDisplayLayout(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display layout";
        setLastError(errorMessage);
        setDisplayLayout(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddDisplayLayoutInList]
  );

  // List display layouts (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<DisplayLayoutFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setDisplayLayouts([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await DisplayLayoutService.list(
          combinedFilter as DisplayLayoutFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newLayouts = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setDisplayLayouts((prev) => {
              const existingIds = new Set(prev.map((dl) => dl.id));
              const uniqueNewLayouts = newLayouts.filter(
                (dl) => !existingIds.has(dl.id)
              );
              return [...prev, ...uniqueNewLayouts];
            });
          } else {
            setDisplayLayouts(newLayouts);

            // Auto-select first layout if enabled and none selected and layouts exist
            if (reset && autoSelectFirstRef.current && newLayouts.length > 0) {
              setSelectedDisplayLayout((prev) => {
                return prev ? prev : newLayouts[0];
              });
            }
          }

          setHasMore(newLayouts.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load display layouts";
          setLastError(errorMessage);
          if (!isAppending) setDisplayLayouts([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display layouts";
        setLastError(errorMessage);
        if (!isAppending) setDisplayLayouts([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many display layouts by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<DisplayLayout[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayLayoutService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedLayouts = Array.isArray(result.data) ? result.data : [];
          loadedLayouts.forEach((layout) => {
            updateOrAddDisplayLayoutInList(layout);
          });
          setLastError(null);
          return loadedLayouts;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load display layouts";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load display layouts";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddDisplayLayoutInList]
  );

  // Create display layout (matching API method name)
  const create = useCallback(
    async (params: DisplayLayoutInput): Promise<DisplayLayout | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const layoutToCreate = {
          ...params,
        };

        const result = await DisplayLayoutService.create(layoutToCreate);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newLayout = result.data[0] as DisplayLayout;
          setDisplayLayout(newLayout);
          setDisplayLayouts((prev) => [newLayout, ...prev]);
          setLastError(null);
          return newLayout;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create display layout";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create display layout";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update display layout (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: DisplayLayoutInput
    ): Promise<DisplayLayout | null> => {
      if (!id) {
        setLastError("Display layout ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await DisplayLayoutService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedLayout = result.data[0] as DisplayLayout;
          setDisplayLayout(updatedLayout);
          updateOrAddDisplayLayoutInList(updatedLayout);
          setLastError(null);
          return updatedLayout;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update display layout";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update display layout";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddDisplayLayoutInList]
  );

  // Remove display layout (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Display layout ID is required for removal");
        return false;
      }

      setRemovingDisplayLayoutId(id);
      setLastError(null);

      try {
        const result = await DisplayLayoutService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setDisplayLayout(null);
          removeDisplayLayoutFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove display layout";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove display layout";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingDisplayLayoutId(null);
      }
    },
    [removeDisplayLayoutFromList]
  );

  // Load more display layouts
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh display layouts
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  useEffect(() => {
    if (autoLoad && isAuthenticated && loadedForUserId != userId) {
      setLoadedForUserId(userId);
      const autoLoadFilters = { ...initialFilters };
      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [
    autoLoad,
    isAuthenticated,
    loadedForUserId,
    userId,
    list,
    pageSize,
    initialSort,
  ]);

  return {
    displayLayout,
    displayLayouts,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingDisplayLayoutId,
    selectedDisplayLayout,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedDisplayLayout,
    reset,
    lastError,
    clearError,
  };
};
